import { CSSObject } from "@emotion/react";
import {
  constants,
  typographyStyles,
  type AnchorStyleOverrides,
  type ButtonStyleOverrides,
  type IconStyleOverrides,
  type AngelouCSS,
} from "@newsela/angelou";

const { fontFamily, textXSObj } = typographyStyles;

const neutral700 = constants.colors.ui.neutral[700];
const neutral600 = constants.colors.ui.neutral[600];
const white = constants.colors.ui.white[50];
const neutral25 = constants.colors.ui.neutral[25];
const neutral100 = constants.colors.ui.neutral[100];

export const contentCardContainerCss: CSSObject = {
  flexGrow: 1,
  height: "100%",
  display: "flex",
  position: "relative",
  flexDirection: "column",
  background: white,
  borderRadius: "1rem",
  border: `1px solid ${constants.colors.ui.neutral[50]}`,

  "> div": {
    borderRadius: "1rem",
    gridTemplateRows: "min-content",
    gridTemplateAreas: `"img"
                        "textual"`,
  },

  "> div > div:nth-of-type(3)": {
    display: "none",
  },
};

export const contentCardLinkCss: AnchorStyleOverrides<AngelouCSS> = {
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    zIndex: 1,
  },
};

export const contentCardTopSectionContainerCss: CSSObject = {
  position: "relative",
  aspectRatio: "16 / 9",
  borderRadius: "1rem 1rem 0 0",
};

export const contentCardImagesContainerCss: CSSObject = {
  position: "relative",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  background: constants.colors.ui.neutral[100],
  borderRadius: "1rem 1rem 0 0",
  aspectRatio: "16 / 9",

  "img:first-of-type:nth-last-of-type(3), img:first-of-type:nth-last-of-type(3) ~ img":
    {
      width: "33.33%",
    },

  "> *:nth-of-type(2)": {
    borderRadius: "0 1.25rem 0 0",
  },

  "> *:nth-of-type(1)": {
    borderRadius: "1rem 0 0 0",
  },

  "img:first-of-type:nth-last-of-type(3) ~ img:nth-of-type(3)": {
    borderRadius: "0 1.25rem 0 0",
  },

  "img:first-of-type:nth-last-of-type(3) ~ img:nth-of-type(2)": {
    borderRadius: "0 0 0 0",
  },

  "img:nth-of-type(3) ~ img:nth-of-type(2)": {
    borderRadius: "0",
  },

  "> *:only-child": {
    width: "100%",
    borderRadius: "1rem 1.5rem 0 0",
  },
};

export const contentCardImageCss: CSSObject = {
  width: "50%",
  background: constants.colors.ui.neutral[100],
  aspectRatio: "16 / 9",
};

export const articleTypeLabelCss: CSSObject = {
  position: "absolute",
  left: 0,
  bottom: 0,
  padding: "0.5rem 0.5rem 0.31rem 0.5rem",
  fontSize: "0.75rem",
  fontWeight: 700,
  borderRadius: "0 0.5rem 0 0",
  height: "1.75rem",
  background: white,
  color: neutral700,
  display: "flex",
  alignItems: "center",
  marginBottom: "-1px",

  svg: {
    height: "0.9rem",
  },
};

export const videoDurationTagCss: CSSObject = {
  position: "absolute",
  display: "flex",
  gap: "0.125rem",
  alignItems: "center",
  bottom: 0,
  right: 0,
  borderRadius: "0.5rem 0 0 0",
  padding: "0.1875rem 0.5rem",
  fontSize: "0.75rem",
  fontWeight: "700",
  color: constants.colors.ui.neutral[700],
  background: white,
};

export const playIconCss = {
  root: {
    width: "0.75rem",
    height: "0.75rem",
  },
};

export const breakingNewsTagCss: CSSObject = {
  position: "absolute",
  bottom: 0,
  left: 0,
  textTransform: "uppercase",
  fontSize: "0.75rem",
  fontWeight: 700,
  padding: "0.5rem",
  borderRadius: "0 0.5rem 0 0",
  color: white,
  background: constants.colors.ui.red[600],
};

export const flaggedContainerCss: CSSObject = {
  position: "absolute",
  left: 0,
  top: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

export const advisoryFlaggedTagCss: CSSObject = {
  borderRadius: "0 0.25rem 0.25rem 0",
  padding: "0 6px",
  fontSize: "0.6875rem",
  fontWeight: "700",
  width: "fit-content",
  color: neutral600,
  background: constants.colors.ui.yellow[500],
};

export const readUntilTagCss: CSSObject = {
  borderRadius: "0 0.25rem 0.25rem 0",
  padding: "0 6px",
  fontSize: "0.6875rem",
  fontWeight: "700",
  color: constants.colors.ui.core[700],
  background: constants.colors.ui.core[50],
};

export const premiumTagCss: CSSObject = {
  borderRadius: "0 0.25rem 0.25rem 0",
  padding: "0 6px",
  width: "fit-content",
  fontSize: "0.6875rem",
  fontWeight: "700",
  color: constants.colors.ui.core[700],
  background: constants.colors.ui.core[50],
};

export const contentCardBookmarkCss = {
  root: {
    position: "absolute !important",
    right: 0,
    top: 0,
    padding: "0.5625rem 0.5625rem",
    fontSize: "0.75rem",
    fontWeight: 700,
    borderRadius: "0 1rem 0rem 0.5rem",
    cursor: "pointer",
    zIndex: 1,
    background: white,
    color: neutral700,
    transition: "transform 300ms ease-out",

    "&:hover": {
      background: white,
    },

    svg: { width: "1.5rem", height: "1.5rem" },
  },
};

export const contentSettingsModeCss = {
  zIndex: 1,
  marginLeft: 0,
  width: "100%",
  padding: "12px 0 0 0",
};

export const contentCardBottomSectionContainerCss: CSSObject = {
  display: "flex",
  gap: "0.25rem",
  flexGrow: 1,
  flexDirection: "column",

  "h1, h2, h3, h4, h5, h6, p": {
    padding: 0,
    margin: 0,
  },

  ul: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
};

export const contentCardInformationContainer: CSSObject = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  paddingTop: 2,
  paddingBottom: 2,
};

export const contentCardDateAndBadgesContainerCss: CSSObject = {
  height: "1rem",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "calc(100% + 1px)",
  justifyContent: "space-between",
};

export const contentCardDateCss: CSSObject = {
  fontSize: "0.75rem",
  fontWeight: 400,
  letterSpacing: "0.00806rem",
  color: neutral600,
};

export const contentCardBadgesContainerCss: CSSObject = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  zIndex: 1,

  li: {
    height: "1.25rem",
  },
};

export const contentCardFormativeBadgeCss: IconStyleOverrides<CSSObject> = {
  root: {
    width: "1rem",
    height: "1rem",
    margin: "0 0.125rem",
    color: neutral600,
  },
};

export const contentCardPowerWordBadgeCss: IconStyleOverrides<CSSObject> = {
  root: {
    width: "1rem",
    height: "1rem",
    margin: "0 0.125rem",

    path: {
      fill: constants.colors.ui.green[300],
      stroke: constants.colors.ui.green[300],
    },
  },
};

export const contentCardSpanishBadgeCss: IconStyleOverrides<CSSObject> = {
  root: {
    width: "1rem",
    height: "1rem",
    margin: "0 0.125rem",

    path: {
      fill: constants.colors.ui.purple[600],
    },
  },
};

export const contentCardTitleCss: CSSObject = {
  ...fontFamily.noto,
  fontSize: "0.9375rem",
  fontWeight: 600,
  lineHeight: "1.17188rem",
  color: neutral700,
};

export const contentCardDescriptionCss: CSSObject = {
  fontSize: "0.875rem",
  fontWeight: 400,
  letterSpacing: "0.00806rem",
  lineHeight: "1.25rem",
  color: neutral600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxHeight: "2.75rem",
};

export const contentCardTagsContainerCss: CSSObject = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  paddingTop: "0.25rem !important",
};

export const contentCardTagCss: CSSObject = {
  ...textXSObj,
  minWidth: "100%",
  textAlign: "center",
  padding: "0.25rem",
  borderRadius: "1.88rem",
  color: neutral700,
  border: `0.5px solid ${neutral100}`,
  background: neutral25,
};

export const contentCardGradeAndLexileCss: CSSObject = {
  ...textXSObj,
  color: constants.colors.ui.neutral[500],
  display: "grid",
  paddingTop: "0.25rem",
  gap: "repeat(auto-fill, 1fr 1fr)",
  flexWrap: "wrap",
};

export const cardActionsContainerCss: CSSObject = {
  display: "flex",
  gap: "0.25rem",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  paddingTop: "0.25rem",
  borderTop: `1px solid ${constants.colors.ui.neutral[50]}`,
  zIndex: 1,
};

export const linkCss = {
  root: {
    padding: "0.5rem",
    textDecoration: "none",
    color: constants.colors.ui.core[700],
  },
};

export const addToAssignmentButtonCss: ButtonStyleOverrides<CSSObject> = {
  root: {
    padding: "0.5rem",
    color: constants.colors.ui.core[700],
  },
};

export const spacedBetweenFlexColumnCss: CSSObject = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  padding: "0.5rem 0.75rem",
  justifyContent: "space-between",
};
